import type { TypeCurveScenarioRow } from 'pages/type-curve-scenarios-list/types';
import { type TypeCurvesListingFilters } from './use-type-curves-listing';
import { useListingQuery } from './hooks';

// Raw API response shape (camelCase from server)
interface ApiScenarioRow {
  id: number;
  name: string;
  basinAreaCount: number;
  developmentType?: string;
  dcaScenario?: { id: number; name: string; href?: string } | null;
  createdAt: string;
  createdBy?: string | null;
  updatedAt: string;
  updatedBy?: string | null;
  resources?: { areaConfigs?: { href: string } } | null;
  href?: string | null;
  auth?: string[] | null;
  status?: string | null;
  /** Area-config extent for click-to-zoom on the listing map. */
  bbox?: [number, number, number, number] | null;
}

// Map camelCase API response → snake_case TypeCurveScenarioRow (matches column accessors)
function mapToRow(r: ApiScenarioRow): TypeCurveScenarioRow {
  return {
    id: r.id,
    name: r.name,
    dca_scenario_id: r.dcaScenario?.id ?? null,
    dca_scenario_name: r.dcaScenario?.name ?? null,
    basin_area_count: r.basinAreaCount,
    development_type: (r.developmentType ??
      '') as TypeCurveScenarioRow['development_type'],
    created_at: r.createdAt,
    created_by: r.createdBy ?? null,
    updated_at: r.updatedAt,
    updated_by: r.updatedBy ?? null,
    area_configs_href: r.resources?.areaConfigs?.href ?? null,
    href: r.href ?? null,
    auth: r.auth ?? null,
    status: r.status ?? null,
    dca_scenario_href: r.dcaScenario?.href ?? null,
    // Must survive the remap — the listing map zooms to this on row click.
    bbox: r.bbox ?? null,
  };
}

export function useTypeCurveScenariosListing(
  filters: TypeCurvesListingFilters,
  options: { enabled: boolean }
) {
  // Unified listing endpoint with the scenarios view; shares the one filter panel
  // + facets across all tabs. (The standalone queries/reservoir/type_curve_scenarios
  // endpoint remains only for the legacy scenarios page.) Rows are remapped from the
  // camelCase API shape to the snake_case columns the table renders.
  const result = useListingQuery<ApiScenarioRow>(
    'scenarios',
    'sc-listing',
    filters,
    options
  );
  return { ...result, rows: result.rows.map(mapToRow) };
}
